import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import useConfirm from 'core/hooks/useConfirm';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

import { getDbFormat } from 'utils/common';
import { getVendorFeedbacksByDate, deleteFeedback } from 'services/feedback';


const VendorFeedbackPage = () => {

  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [ filterDates, setFilterDates] = React.useState([new Date(), new Date()]);

  const [vendorFeedback, setVendorFeedback] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const onActionBtnClick = (e, type, feedbackId) => {
    e.preventDefault();
    if(type === 'delete'){
      onDeleteVendorFeedback(feedbackId)
    }
  };

  const defaultFilterValue = [
    { name: 'name', operator: 'contains', type: 'string', value: '' },
    { name: 'feedback', operator: 'contains', type: 'string', value: '' },
    { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
  ];

  const columns = [
    { name: 'name', header: 'Name', width: 300},
    { name: 'feedback', header: 'Feedback', width: 600},
    { name: 'createdOn', header: 'Created On', width: 200},
    { name: 'actions', header: 'Actions', width: 150, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Delete Feedback" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'delete', data?.id)}>
                <DeleteForeverIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      )
    }},
  ];

  useEffect(() => {
    getVendorFeedback();
  }, []);


  function getVendorFeedback() {
    const [fromDate,  toDate] = filterDates;
    const data = {fromDate: getDbFormat(fromDate), toDate: getDbFormat(toDate)};
    getVendorFeedbacksByDate(data).then(({response, error}) => {
      if(response){;
        setVendorFeedback([...(response || [])]);
      } else {
        setVendorFeedback([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function onDateAccepted(data) {
    const [fromDate,  toDate] = data;
    if((fromDate && toDate)) {
      getVendorFeedback();
    }else{
      enqueueSnackbar(`Select ${fromDate === null ? 'From Date' : 'To Date'}`, {variant: 'error'});
    }
  }

  function onDeleteVendorFeedback(feedbackId) {
    confirm({ title: 'Delete vendor feedback?', description: 'Are you sure you want proceed?' })
    .then(() => { 
      deleteVendorFeedback(feedbackId);
    }).catch((e)=>{})
  }

  function deleteVendorFeedback(id) {
    deleteFeedback({id}).then(({response, error}) => {
      if(response){
        enqueueSnackbar((response?.message || 'Vendor feedback deleted successfully'), {variant: 'success'});
        getVendorFeedback();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Vendor Feedback
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} xl={12} lg={12} >
            <Paper elevation={0} sx={{p:1, bgcolor: "#f3f3f3"}}>
              <Typography variant="subtitle2" gutterBottom component="div">
                Filters
              </Typography>
              <Stack direction="row" spacing={1}>
                <Box sx={{mt: 2}}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileDateRangePicker
                      inputFormat="dd/MM/yyyy"
                      startText="From Date"
                      endText="To Date"
                      value={filterDates}
                      // clearable={true}
                      onChange={(newValue) => {
                        setFilterDates(newValue);
                      }}
                      onAccept={(data) => onDateAccepted(data)}
                      renderInput={(startProps, endProps) => (
                        <React.Fragment>
                          <TextField {...startProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}}/>
                          <Box sx={{ mx: 2 }}> to </Box>
                          <TextField {...endProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}} />
                        </React.Fragment>
                      )}
                      />
                  </LocalizationProvider>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{  mt: 3 ,  maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                // handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={vendorFeedback}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
    </Paper>
  );
}
 
export default React.memo(VendorFeedbackPage);
